import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Typography, Box } from "@mui/material";
import { Add, Edit } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import FormDrawer from "components/Shared/FormDrawer";
import AppTable from "components/Shared/AppTable";
import { BaseTabs } from "components/Shared/BaseTabs";
import {
  useAddWorkScheduleMutation,
  useUpdateWorkScheduleMutation,
  useLazyListDriverScheduleQuery,
} from "store/services/driverApi";
import { useAuth } from "hooks";
import { useTablePagination } from "hooks";
import WorkScheduleForm from "./DriverWorkScheduleForm";

const WorkScheduleTabs = () => {
  const { id } = useParams();
  const { loggedInUser, isDriver } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const driverId = isDriver ? loggedInUser.typeRef : id;

  const scheduleTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const dayOrder = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };

  
  const [addSchedule] = useAddWorkScheduleMutation();
  const [updateSchedule] = useUpdateWorkScheduleMutation();

  const [fetchSchedules, { data, isLoading, isFetching, error }] =
    useLazyListDriverScheduleQuery();

  React.useEffect(() => {
    fetchSchedules({
      driverId,
      page: currentPage,
      limit: perPage,
      active: activeTab === "Active",
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchSchedules, keyword, perPage, driverId]);

  const handleSubmit = async (formData) => {
    try {
      if (selectedSchedule) {
        await updateSchedule({
          driverId,
          scheduleId: selectedSchedule._id,
          ...formData,
        }).unwrap();
        toast.success("Driver schedule updated successfully");
      } else {
        await addSchedule({ driverId, ...formData }).unwrap();
        toast.success("Driver schedule added successfully");
      }
      setShowForm(false);
      setSelectedSchedule(null);
    } catch (err) {
      toast.error("An error occurred");
    }
  };

  // Extract existing days and dates for Fixed and Flexible schedules
  const existingDays = (data?.driverSchedules || [])
    .filter((schedule) => schedule.scheduleType === "Fixed" && schedule.isActive)
    .map((schedule) => schedule.day);

  const existingDates = (data?.driverSchedules || [])
    .filter((schedule) => schedule.scheduleType === "Flexible" && schedule.isActive)
    .map((schedule) => {
      if (schedule.date) {
        const date = new Date(schedule.date);
        return isNaN(date.getTime()) ? null : date.toISOString().split("T")[0];
      }
      return null;
    })
    .filter((date) => date !== null);

  const columns = [
    {
      name: "Schedule Type",
      selector: (row) => row.scheduleType,
      sortable: true,
    },
    {
      name: "Day/Date",
      selector: (row) => (row.scheduleType === "Fixed" ? row.day : row.date ? new Date(row.date).toISOString().split("T")[0] : "N/A"),
      sortable: true,
      sortFunction: (a, b) => {
        if (a.scheduleType === "Fixed" && b.scheduleType === "Fixed") {
          return dayOrder[a.day] - dayOrder[b.day];
        } else if (a.scheduleType === "Flexible" && b.scheduleType === "Flexible") {
          return new Date(a.date) - new Date(b.date);
        } else {
          return a.scheduleType.localeCompare(b.scheduleType);
        }
      },
    },
    {
      name: "Start Time",
      selector: (row) => row.startTime,
      cell: (row) => <span>{row.startTime || "N/A"}</span>,
      sortable: true,
    },
    {
      name: "End Time",
      selector: (row) => row.endTime,
      cell: (row) => <span>{row.endTime || "N/A"}</span>,
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Button
          onClick={() => {
            setSelectedSchedule(row);
            setShowForm(true);
          }}
        >
          <Edit fontSize="small" />
        </Button>
      ),
    },
  ];

  const breadcrumbs = [{ title: "Work Schedule" }];

  return (
    <>
      {isDriver ? (
        <BreadcrumbLayout
          breadcrumbs={breadcrumbs}
          // headerActions={
          //   <Button
          //     variant="contained"
          //     startIcon={<Add />}
          //     onClick={() => {
          //       setSelectedSchedule(null);
          //       setShowForm(true);
          //     }}
          //   >
          //     Add Schedule
          //   </Button>
          // }
        >
          <BreadcrumbLayout.Error error={error} />
          <BreadcrumbLayout.Paper>
            <BaseTabs tabs={scheduleTabs} defaultValue={activeTab} />
            <AppTable
              columns={columns}
              data={(data?.driverSchedules || []).slice()}
              progressPending={isLoading || isFetching}
              paginationServer
              paginationPerPage={perPage}
              paginationTotalRows={data?.totalItems || 0}
              paginationDefaultPage={currentPage}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              noDataComponent={
                <Typography py={3} textAlign="center" color="textSecondary">
                  No schedules found
                </Typography>
              }
            />
          </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", marginRight: "10px" }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setSelectedSchedule(null);
                setShowForm(true);
              }}
            >
              Add Schedule
            </Button>
          </Box>
          <BaseTabs tabs={scheduleTabs} defaultValue={activeTab} />
          <AppTable
            columns={columns}
            data={(data?.driverSchedules || []).slice()}
            progressPending={isLoading || isFetching}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={data?.totalItems || 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            noDataComponent={
              <Typography py={3} textAlign="center" color="textSecondary">
                No schedules found
              </Typography>
            }
          />
        </Box>
      )}

      <FormDrawer
        open={showForm}
        onClose={() => setShowForm(false)}
        title={`${selectedSchedule ? "Edit" : "Add"} Driver Schedule`}
      >
        <WorkScheduleForm
          workSchedule={selectedSchedule}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
          isBusy={false}
          existingDays={existingDays}
          existingDates={existingDates}
        />
      </FormDrawer>
    </>
  );
};

export default WorkScheduleTabs;